'use client'; // Add this at the top

import React, { useState } from 'react';
import { useAOS } from '@/hooks/useAOS';
import { sanitizeHtml } from '@/lib/sanitize';

// FaqItem component for individual FAQ items
const FaqItem = ({ question, answer, isOpen, onToggle, isLast, hidden }) => {
  return (
    <div
      className={`transition-all duration-200 py-[16px]  border-b-2 border-[#D8E9FF] cursor-pointer ${isLast ? 'border-none' : ''} ${hidden ? 'hidden' : ''}`}
    >
      <button
        type="button"
        className="flex items-center text-start justify-between h-fit w-full"
        onClick={onToggle}
      >
        <span
          className={`${isOpen ? 'text-[#195AFF]' : 'text-[#090D28]'
            } schibsted-grotesk text-[18px] md:text-[20px] font-[600] leading-[30px]`}
        >
          {question}
        </span>
        <div className='w-[20px]'>
          <svg
            className={`w-6 h-6 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#1558BC]' : 'text-[#090D28]'
              }`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          }`}
      >
        <div
          className="overflow-hidden text-[#2D3238] text-start mt-[8px] schibsted-grotesk text-[16px] font-normal leading-[150%]"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(answer) }}
        />
      </div>

    </div>
  );
};

// FaqSection component
const FaqSection = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState(0); // Independent state for each section
  const [showAll, setShowAll] = useState(false);

  // Previously `.slice(0, 5)` kept everything past the 5th question out of the DOM entirely
  // (not just visually collapsed) until "Show More" was clicked, which meant pages with more
  // than 5 FAQs had a FAQPage schema that listed questions no crawler could actually find in the
  // rendered HTML — a real mismatch, not just a false positive. All questions now render; only
  // the ones past the limit get the `hidden` class, the same "present in the DOM, not visible"
  // pattern already used for each item's own open/closed answer.

  // Toggle function for smooth open/close
  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useAOS();

  return (
    <div >
      <section className="flex md:flex-row flex-col justify-center max-w-5xl mx-auto px-[16px] items-center">
        <div>
          <div>
            <h2 className="mt-[80px] text-[#090D28] flex items-center justify-center text-center text-[32px] w-full mx-auto md:text-[48px] font-extrabold leading-[120%] font-NeueMontreal md:text-nowrap text-wra">
              Your questions&nbsp;Our Answers
            </h2>
          </div>
          <div className=" mx-auto max-w-4xl mx-auto md:px-0 px-4">
            <div className="max-w-3xl mx-auto mt-[24px] md:mt-[27px]">
              {faqs.map((faq, index) => (
                <FaqItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onToggle={() => toggleFaq(index)}
                  isLast={index === faqs.length - 1}
                  hidden={!showAll && index >= 5}
                />
              ))}

              {faqs.length > 5 && (
                <div className="text-center mt-4 mb-4">
                  <button
                    onClick={() => setShowAll(!showAll)}
                    className="mt-2 inline-flex h-[48px] px-[24px] py-[16px] justify-center items-center flex-shrink-0 text-[#195AFF] text-[16px] md:text-[18px] font-semibold leading-[120%] schibsted-grotesk rounded-[8px] bg-white shadow-[-11px_34px_10px_0px_rgba(25,90,255,0),-7px_21px_9px_0px_rgba(25,90,255,0.01),-4px_12px_8px_0px_rgba(25,90,255,0.05),-2px_5px_6px_0px_rgba(25,90,255,0.09),0px_1px_3px_0px_rgba(25,90,255,0.10)]"
                  >
                    {showAll ? 'Show Less' : 'Show More'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

      </section>
    </div>
  );
};

export default FaqSection;

